import React, { Component } from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';
import { DropTarget } from 'react-dnd';
import flatten from 'lodash.flatten';
import styled from 'styled-components';
import cn from 'classnames';
import moment from '~/Providers/Moment';

import { setDayType } from '~/Redux/Modules/DateRange';
import { openPresetModal, updateEvent } from '~/Redux/Modules/Events';
import { findCalendarInList, mapCalendarsToList } from '~/Redux/Modules/CalendarAccounts';
import { addDay, clearDays } from '~/Redux/Modules/MonthSelect';
import CalendarEvents from '~/Components/Global/CalendarEvents/CalendarEvents';
import {
  DATE_TIME_FORMAT,
  DATE_FORMAT,
  TIME_FORMAT,
  TABLET_WIDTH,
  DEFAULT_TIME_ZONE,
} from '~/Redux/Constants';
import { MONTH_VIEW } from '~/Config/CalendarViews';
import StickerDrop from '~/Containers/Global/Stickers/StickerDrop/StickerDrop';
import { MONTH_EVENT } from '~/Config/DragTypes';
import { onlyTime } from '~/Components/Global/CalendarEvents/FormatDateTime';
import { fontNameForEvents } from '~/Redux/Modules/UserOptions';
import { eventFontSizeHelper } from '~/Components/Global/FontWrapper/FontWrapper';
import { DrawingTarget } from '~/Components/Global/Drawing/DrawingTarget';

require('./Day.scss');

export const eventTarget = {
  canDrop: ({ day }, monitor) => {
    const previous = monitor.getItem().event;
    return moment(previous.start, DATE_TIME_FORMAT).format(DATE_FORMAT) !== day.format(DATE_FORMAT);
  },
  drop: ({ updateEvent, day, currentView, currentViewStart }, monitor) => {
    const event = monitor.getItem().event;
    var newStart = moment(
      `${day.format(DATE_FORMAT)} ${moment(event.start, DATE_TIME_FORMAT).format(TIME_FORMAT)}`,
      DATE_TIME_FORMAT
    );
    var newEnd = newStart
      .clone()
      .add(moment(event.end, DATE_TIME_FORMAT).diff(moment(event.start, DATE_TIME_FORMAT)));
    updateEvent(
      {
        ...event,
        start: newStart.format(DATE_TIME_FORMAT),
        end: newEnd.format(DATE_TIME_FORMAT),
        displayStart: newStart.format(DATE_TIME_FORMAT),
        displayEnd: newEnd.format(DATE_TIME_FORMAT),
        confirm: 'this_event',
      },
      currentView,
      currentViewStart
    );
  },
};

const dropCollect = (connect, monitor) => ({
  connectDropTarget: connect.dropTarget(),
  isOver: monitor.isOver(),
  isDragging: monitor.isDragging,
  canDrop: monitor.canDrop(),
  dragItem: monitor.getItem(),
});

@DropTarget(MONTH_EVENT, eventTarget, dropCollect)
export class Day extends Component {
  constructor() {
    super();
    this.state = {
      expanded: false,
      readyToSelect: false,
    };
  }
  isToday = () => {
    return moment().isSame(this.props.day, 'day');
  };

  toggle = (e, change) => {
    e.stopPropagation();
    this.setState({ expanded: change });
  };

  handleMakeAllDayEvent = e => {
    if (
      e.target.classList.contains('sticker-drop-wrapper') ||
      e.target.classList.contains('sticker')
    ) {
      e.stopPropagation();
      e.preventDefault();
    } else {
      const { day, openPresetModal, dayView, changingSticker } = this.props;
      if (changingSticker) return;
      if (window.innerWidth < TABLET_WIDTH) {
        return dayView(day);
      }
      openPresetModal({
        start: day.startOf('day').format(DATE_TIME_FORMAT),
        end: day
          .clone()
          .add(1, 'day')
          .startOf('day')
          .format(DATE_TIME_FORMAT),
        isAllDay: true,
        forceUpdate: true,
      });
    }
  };

  calcMoreLength = () => {
    // if (window.innerWidth < TABLET_WIDTH) return 4;
    let moreLength = 2;
    if (this.sizeRef) {
      moreLength = Math.max(parseInt((this.sizeRef.offsetHeight - 28) / 18), moreLength);
    }
    return moreLength;
  };

  generateMoreToggle = (currentAllDayEvents, day) => {
    const moreLength = this.calcMoreLength();
    if (!currentAllDayEvents) return;
    const more = currentAllDayEvents.slice(moreLength).filter(eventRow => eventRow[day.weekday()])
      .length;
    if (!this.state.expanded && more > 0) {
      return (
        <div
          onClick={e => this.toggle(e, true)}
          className="more-events"
          onMouseDown={e => e.stopPropagation()}
        >
          <span className="tablet">+ {more} MORE</span>
          <span className="mobile">+ {more}</span>
        </div>
      );
    }
  };

  generateLessToggle = () => {
    if (this.state.expanded) {
      return (
        <span
          key="close-more"
          className="close-more"
          onClick={e => this.toggle(e, false)}
          onMouseDown={e => e.stopPropagation()}
        >
          x
        </span>
      );
    }
  };

  generateCalendarEvents = () => {
    const { events, day, allDayEvents, userTimeZone, dayView, weekNumber } = this.props;
    if (!Object.keys(events).length && allDayEvents.length) return false;

    return [
      <CalendarEvents
        day={day}
        key="a"
        userTimeZone={userTimeZone}
        expanded={this.state.expanded}
        events={flatten(Object.values(events))}
        moreLength={this.calcMoreLength()}
        currentAllDayEvents={allDayEvents[weekNumber]}
        clickBehavior={window.innerWidth < TABLET_WIDTH ? () => dayView(day) : false}
      />,
      this.generateMoreToggle(allDayEvents[weekNumber], day),
    ];
  };

  handleNavigateToDay = e => {
    const { day } = this.props;
    e.stopPropagation();
    this.props.setDayType(day);
  };

  componentWillUnmount = () => {
    document.body.removeEventListener('mouseup', this.onMouseUp);
  };

  onMouseDown = e => {
    if (window.innerWidth < TABLET_WIDTH) return;
    const { isDragging } = this.props;
    if (
      isDragging ||
      e.target.classList.contains('all-day-event') ||
      e.target.classList.contains('sticker') ||
      e.target.classList.contains('sticker-actions') ||
      e.target.classList.contains('sticker-image') ||
      e.target.classList.contains('sticker-close') ||
      e.target.classList.contains('day-number') ||
      e.target.classList.contains('event-text')
    )
      return;
    this.setState({ readyToSelect: true });
    document.body.addEventListener('mouseup', this.onMouseUp);
  };

  onMouseMove = () => {
    if (!this.props.isSelecting && !this.state.readyToSelect) return;
    const { day, addDayToSelection } = this.props;
    addDayToSelection(day);
  };

  onMouseUp = () => {
    if (this.props.changingSticker) return;
    this.setState({ readyToSelect: false });
    document.body.removeEventListener('mouseup', this.onMouseUp);
    if (!this.props.isSelecting) return;
    const {
      selection: { start, end },
      openPresetModal,
    } = this.props;
    openPresetModal({
      start: start.format(DATE_TIME_FORMAT),
      end: end
        .clone()
        .add(1, 'day')
        .format(DATE_TIME_FORMAT),
      isAllDay: true,
      forceUpdate: true,
    });
    this.props.clearSelection();
  };

  isSelected = () => {
    const {
      day,
      selection: { start, end },
    } = this.props;
    if (!start || !end) return;
    return start && end && !day.isBefore(start) && !day.isAfter(end);
  };

  render() {
    const {
      day,
      today,
      minDate,
      connectDropTarget,
      isOver,
      canDrop,
      dragItem,
      fontNameForEvents,
      isInPageBreak,
    } = this.props;
    const defaultCalendar = { color: '#efefef', ...this.props.defaultCalendar };

    if (!day) return <div className="month-day no-date" />;

    let className = 'month-day';

    if (day && day.format(DATE_TIME_FORMAT) === today.format(DATE_TIME_FORMAT))
      className += ' current-date';

    const outOfRange = minDate ? day.isBefore(minDate.clone().startOf('day')) : false;

    if (outOfRange) className += ' out-of-range';

    if (this.isSelected()) className += ' selected';

    return connectDropTarget(
      <div
        onMouseDown={this.onMouseDown}
        onMouseMove={this.onMouseMove}
        className={className}
        onClick={this.handleMakeAllDayEvent}
        ref={r => (this.sizeRef = r)}
      >
        {this.isToday() && (
          <div
            className={cn('border-element', {
              'current-day': this.isToday(),
              'border-element-in-page-break': isInPageBreak,
            })}
            style={{ border: `2.0px solid ${defaultCalendar.color}` }}
          />
        )}
        {this.isSelected() && (
          <div className="select-element" style={{ background: defaultCalendar.color }} />
        )}

        {isOver && canDrop && dragItem.event.isAllDay && (
          <div
            className="drop-element"
            style={{
              background:
                dragItem.event.color || (dragItem.event.calendar && dragItem.event.calendar.color),
              fontFamily: fontNameForEvents,
              fontSize: eventFontSizeHelper(fontNameForEvents),
            }}
          >
            {dragItem.event.summary}
          </div>
        )}

        {isOver && canDrop && !dragItem.event.isAllDay && (
          <div
            className="drop-element"
            style={{
              background: 'white',
              fontFamily: fontNameForEvents,
              fontSize: eventFontSizeHelper(fontNameForEvents),
              color:
                dragItem.event.color || (dragItem.event.calendar && dragItem.event.calendar.color),
            }}
          >{`${onlyTime(dragItem.event.displayStart)} ${dragItem.event.summary}`}</div>
        )}

        <div className={this.state.expanded ? 'month-day-wrapper expanded' : 'month-day-wrapper'}>
          <DrawingTarget date={day.format(DATE_FORMAT)}>
            <StickerDrop view={MONTH_VIEW} date={day.format(DATE_FORMAT)}>
              {this.generateLessToggle()}
              <CurrentDay
                color={defaultCalendar.color}
                key="day-number"
                onClick={this.handleNavigateToDay}
                className={cn('day-number', { 'current-day': this.isToday() })}
              >
                {day.date()}
              </CurrentDay>
              {this.generateCalendarEvents()}
            </StickerDrop>
          </DrawingTarget>
        </div>
      </div>
    );
  }
}

const CurrentDay = styled.div`
  &.current-day:before {
    background-color: ${({ color }) => color} !important;
  }
`;

Day.propTypes = {
  day: propTypes.oneOfType([propTypes.object, propTypes.number]),
  today: propTypes.oneOfType([propTypes.object, propTypes.number]),
  minDate: propTypes.object,
  events: propTypes.object,
  openPresetModal: propTypes.func,
  setDayType: propTypes.func,
  currentMonth: propTypes.number,
  allDayEvents: propTypes.array,
  dayView: propTypes.func,
  userTimeZone: propTypes.string,
  isDragging: propTypes.bool,
  isSelecting: propTypes.bool,
  addDayToSelection: propTypes.func,
  selection: propTypes.object,
  clearSelection: propTypes.func,
  connectDropTarget: propTypes.func,
  isOver: propTypes.bool,
  canDrop: propTypes.bool,
  dragItem: propTypes.object,
  defaultCalendar: propTypes.object,
  fontNameForEvents: propTypes.string,
  weekNumber: propTypes.number.isRequired,
  changingSticker: propTypes.bool,
  isInPageBreak: propTypes.bool,
};

const mapStateToProps = ({
  Events,
  DateRange,
  UserOptions,
  CalendarAccounts,
  MonthSelect,
  Fonts,
  DroppedStickers,
}) => {
  const calendars = mapCalendarsToList(CalendarAccounts);
  return {
    events: Events.formattedData,
    today: DateRange.today,
    currentMonth: DateRange.month,
    start: DateRange.start.clone(),
    end: DateRange.end.clone(),
    defaultCalendar:
      findCalendarInList(calendars, UserOptions.data.calendar_id) ||
      calendars.filter(calendar => calendar.active)[0] ||
      {},
    currentView: DateRange.currentType,
    currentViewStart: DateRange.start.clone(),
    isSelecting: MonthSelect.selecting,
    selection: MonthSelect,
    userTimeZone: moment.tz.guess() || DEFAULT_TIME_ZONE,
    fontNameForEvents: fontNameForEvents({ UserOptions, Fonts }),
    changingSticker: DroppedStickers.isChanging,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    setDayType: day => dispatch(setDayType(day)),
    openPresetModal: update => dispatch(openPresetModal(update)),
    updateEvent: (data, currentView, currentViewStart) =>
      dispatch(updateEvent(data, currentView, currentViewStart)),
    dayView: currentDay => dispatch(setDayType(currentDay.clone())),
    addDayToSelection: day => dispatch(addDay(day)),
    clearSelection: () => dispatch(clearDays()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Day);
